import { call, put } from "redux-saga/effects";
import Swal from "sweetalert2";

import {
    fetchCertificatsSuccess,
    fetchCertificatsFail,
    fetchCertificatPlaceholdersSuccess,
    fetchCertificatPlaceholdersFail,
    createCertificatSuccess,
    createCertificatFail,
    updateCertificatSuccess,
    updateCertificatFail,
    deleteCertificatSuccess,
    deleteCertificatFail,
    createCertificatVersionSuccess,
    createCertificatVersionFail,
    updateCertificatLifecycleSuccess,
    updateCertificatLifecycleFail,
    generateCertificatPdfSuccess,
    generateCertificatPdfFail,
    setCertificatsLoading,
} from "../actions/certificatsActions";

import { API_BASE_URL, deleteRequest, extractErrorMessage, getRequest, patchRequest, postRequest, putRequest } from "../../../api";

const getToken = () => sessionStorage.getItem("token");
const getUid = () => sessionStorage.getItem("uid");

const buildCertificatFormData = (payload) => {
    const fd = new FormData();
    fd.append("payload", JSON.stringify(payload?.data || {}));
    if (payload?.image) {
        fd.append("image", payload.image);
    }
    return fd;
};

function* fetchAllCertificats() {
    const url = `${API_BASE_URL}/api/certificats`;
    const token = getToken();
    const response = yield call(getRequest, { url, token });
    yield put(fetchCertificatsSuccess(response.data));
}

export function* fetchCertificatsSaga() {
    yield put(setCertificatsLoading(true));
    try {
        yield* fetchAllCertificats();
    } catch (error) {
        yield put(fetchCertificatsFail(extractErrorMessage(error, "Erreur chargement certificats")));
    } finally {
        yield put(setCertificatsLoading(false));
    }
}

export function* fetchCertificatPlaceholdersSaga() {
    const url = `${API_BASE_URL}/api/certificats/placeholders`;
    const token = getToken();
    try {
        const response = yield call(getRequest, { url, token });
        yield put(fetchCertificatPlaceholdersSuccess(response.data));
    } catch (error) {
        yield put(fetchCertificatPlaceholdersFail(extractErrorMessage(error, "Erreur chargement placeholders")));
    }
}

export function* createCertificatSaga(action) {
    const token = getToken();
    const url = `${API_BASE_URL}/api/certificats`;
    yield put(setCertificatsLoading(true));
    try {
        const response = yield call(postRequest, {
            url,
            token,
            data: buildCertificatFormData(action.payload),
        });
        yield put(createCertificatSuccess(response.data));
        yield* fetchAllCertificats();
        yield call([Swal, Swal.fire], { icon: "success", text: "Modèle enregistré", timer: 1500, showConfirmButton: false });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur création certificat");
        yield put(createCertificatFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message });
    } finally {
        yield put(setCertificatsLoading(false));
    }
}

export function* updateCertificatSaga(action) {
    const token = getToken();
    const { id, payload } = action.payload;
    const url = `${API_BASE_URL}/api/certificats/${id}`;
    yield put(setCertificatsLoading(true));
    try {
        const response = yield call(putRequest, {
            url,
            token,
            data: buildCertificatFormData(payload),
        });
        yield put(updateCertificatSuccess(response.data));
        yield* fetchAllCertificats();
        yield call([Swal, Swal.fire], { icon: "success", text: "Modèle mis à jour", timer: 1500, showConfirmButton: false });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur modification certificat");
        yield put(updateCertificatFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message });
    } finally {
        yield put(setCertificatsLoading(false));
    }
}

export function* deleteCertificatSaga(action) {
    const token = getToken();
    const { id } = action.payload;
    const url = `${API_BASE_URL}/api/certificats/${id}`;
    yield put(setCertificatsLoading(true));
    try {
        yield call(deleteRequest, { url, token });
        yield put(deleteCertificatSuccess(id));
        yield* fetchAllCertificats();
        yield call([Swal, Swal.fire], { icon: "success", text: "Modèle supprimé", timer: 1500, showConfirmButton: false });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur suppression certificat");
        yield put(deleteCertificatFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message });
    } finally {
        yield put(setCertificatsLoading(false));
    }
}

export function* createCertificatVersionSaga(action) {
    const token = getToken();
    const url = `${API_BASE_URL}/api/certificats/${action.payload.id}/versions`;
    yield put(setCertificatsLoading(true));
    try {
        const response = yield call(postRequest, { url, token, data: {} });
        yield put(createCertificatVersionSuccess(response.data));
        yield* fetchAllCertificats();
        yield call([Swal, Swal.fire], { icon: "success", text: "Nouvelle version créée", timer: 1500, showConfirmButton: false });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur création version");
        yield put(createCertificatVersionFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message });
    } finally {
        yield put(setCertificatsLoading(false));
    }
}

function* patchLifecycle(url, body, successText) {
    const token = getToken();
    const uid = getUid();
    yield put(setCertificatsLoading(true));
    try {
        const response = yield call(patchRequest, { url, token, uid, data: body });
        yield put(updateCertificatLifecycleSuccess(response.data));
        yield* fetchAllCertificats();
        yield call([Swal, Swal.fire], { icon: "success", text: successText, timer: 1400, showConfirmButton: false });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur de mise à jour");
        yield put(updateCertificatLifecycleFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message });
    } finally {
        yield put(setCertificatsLoading(false));
    }
}

export function* updateCertificatActivationSaga(action) {
    yield* patchLifecycle(
        `${API_BASE_URL}/api/certificats/${action.payload.id}/activation`,
        { active: action.payload.active },
        action.payload.active ? "Modèle activé" : "Modèle désactivé",
    );
}

export function* updateCertificatPublicationSaga(action) {
    yield* patchLifecycle(
        `${API_BASE_URL}/api/certificats/${action.payload.id}/publication`,
        { publicationStatus: action.payload.publicationStatus },
        action.payload.publicationStatus === "PUBLISHED" ? "Version publiée" : "Version remise en brouillon",
    );
}

export function* updateCertificatArchiveSaga(action) {
    yield* patchLifecycle(
        `${API_BASE_URL}/api/certificats/${action.payload.id}/archive`,
        { archived: action.payload.archived },
        action.payload.archived ? "Modèle archivé" : "Modèle restauré",
    );
}

export function* generateCertificatPdfSaga(action) {
    const token = getToken();
    const { id, ...payload } = action.payload;
    const url = `${API_BASE_URL}/api/certificats/${id}/generate`;
    try {
        const response = yield call(postRequest, { url, token, data: payload });
        yield put(generateCertificatPdfSuccess(response.data));
        const fileUrl = response.data?.document?.fileUrl;
        if (fileUrl) {
            window.open(fileUrl, "_blank");
        }
        yield call([Swal, Swal.fire], { icon: "success", text: "Certificat généré", timer: 1700, showConfirmButton: false });
    } catch (error) {
        const message = extractErrorMessage(error, "Erreur génération PDF");
        yield put(generateCertificatPdfFail(message));
        yield call([Swal, Swal.fire], { icon: "error", text: message });
    }
}
